<template>
	<div>
		<b-row class="justify-content-lg-center">
			<b-col :lg=8 :xl=8>
				<b-card-title>{{ $t('task_title_edit') }}</b-card-title>
                <b-form @submit="submit" validate>
					<b-form-group
						id="form_label_wash_station"
						:label="$t('task_form_label_wash_station')"
						label-for="form_wash_station"
					>
						<v-select
							:options="washStations"
							:reduce="washStation => washStation.id"
							label="name"
							:placeholder="$t('task_form_placeholder_wash_station')"
							v-model="formWashStation"
						>
							<template #search="{attributes, events}">
								<input
									class="vs__search"
									:required="!formWashStation"
									v-bind="attributes"
									v-on="events"
								/>
							</template>
							<div slot="no-options">{{ $t('app_vselect_no_options_found') }}</div>
						</v-select>
					</b-form-group>
					<b-form-group
						id="form_label_worker"
						:label="this.$t('task_form_label_worker')"
						label-for="form_worker"
					>
						<v-select
							:options="filterWorkers(workers)"
							:reduce="worker => worker.id"
							:placeholder="$t('task_form_placeholder_worker')"
							v-model="formWorker"
							:filter="workerSelectFilter"
							label="lastname"
						>
							<template slot="option" slot-scope="option">
								{{ option.firstname }} {{option.lastname}}
							</template>
							<template slot="selected-option" slot-scope="option">
								{{ option.firstname }} {{option.lastname}}
							</template>
							<template #search="{attributes, events}">
								<input
									class="vs__search"
									:required="!formWorker"
									v-bind="attributes"
									v-on="events"
								/>
							</template>
							<div slot="no-options">{{ $t('app_vselect_no_options_found') }}</div>
						</v-select>
					</b-form-group>
					<b-form-group
						id="form_label_start_time"
						:label="$t('task_form_label_start_time')"
						label-for="form_startTime"
					>
						<b-input-group>
							<date-picker 
								v-model="formStartTime" 
								:config="configs.start" 
								v-on:dp-change="onStartChange" 
								:placeholder="this.$t('task_form_placeholder_start_time')" 
								required
							/>
							<b-input-group-append>
								<b-button variant="secondary" @click="setStartTime" :disabled="formStartTime ? true : false">{{ this.$t('task_form_button_set_start_time') }}</b-button>
								<b-button variant="outline-danger" @click="cancelStartTime" :disabled="!formStartTime ? true : false"><b-icon icon="x" scale="1.25" /></b-button>
							</b-input-group-append>
						</b-input-group>
					</b-form-group>
					<b-form-group
						id="form_label_end_time"
						:label="this.$t('task_form_label_end_time')"
						label-for="form_endTime"
					>
						<b-input-group>
							<date-picker 
								v-model="formEndTime" 
								:config="configs.end" 
								v-on:dp-change="onEndChange" 
								:placeholder="this.$t('task_form_placeholder_end_time')" 
								required
							/>
							<b-input-group-append>
								<b-button variant="secondary" @click="setEndTime" :disabled="!formStartTime || formEndTime ? true : false">{{ $t('task_form_button_set_end_time') }}</b-button>
								<b-button variant="outline-danger" @click="cancelEndTime" :disabled="!formEndTime ? true : false"><b-icon icon="x" scale="1.25" /></b-button>
							</b-input-group-append>
						</b-input-group>
					</b-form-group>
					<b-alert show variant="light" v-if="taskDuration && formStartTime && formEndTime">
						{{ $t('task_form_task_duration') }} {{ taskDuration }}
					</b-alert>
					<hr>
					<b-form-group
						id="form_label_customer"
						:label="this.$t('task_form_label_customer')"
						label-for="form_customer"
					>
						<b-form-row>
							<b-col>
								<v-select
									:options="filterCustomers(customers)"
									:reduce="customer => customer.id"
									label="name"
									:placeholder="$t('task_form_placeholder_customer')"
									v-model="formCustomer"
								>
									<template #search="{attributes, events}">
										<input
											class="vs__search"
											:required="formCustomer === null"
											v-bind="attributes"
											v-on="events"
										/>
									</template>
									<div slot="no-options">{{ $t('app_vselect_no_options_found') }}</div>
								</v-select>
							</b-col>
							<b-col :lg=6 v-if="!formCustomer">
								<b-form-input
									id="form_new_customer"
									v-model="formNewCustomer"
									required
									:placeholder="$t('task_form_placeholder_new_customer')"
								/>
							</b-col>
						</b-form-row>
					</b-form-group>
					<b-form-group
						id="form_label_product"
						:label="$t('task_form_label_product')"
					>
						<b-form-row class="mb-2" v-for="(row, index) in formProducts" :key="index">
							<b-col :cols=8>
								<v-select
									:options="filterProducts(products)"
									:reduce="product => product.id"
									label="name"
									:placeholder="$t('task_form_placeholder_product')"
									v-model="row.product"
									:filter="productSelectFilter"
									@input="setRowUnit(index, row.product)"
									:selectable="option => !checkSelectedProducts(option.id)"
								>
									<template slot="option" slot-scope="option" v-if="option.code && option.name">
										{{ option.code }} - {{ option.name }}
									</template>
									<template slot="option" slot-scope="option" v-else>
										{{ option.name }}
									</template>
									<template slot="selected-option" slot-scope="option" v-if="option.code && option.name">
										{{ option.code }} - {{ option.name }}
									</template>
									<template slot="selected-option" slot-scope="option" v-else>
										{{ option.name }}
									</template>
									<div slot="no-options">{{ $t('app_vselect_no_options_found') }}</div>
								</v-select>
							</b-col>
							<b-col :cols=2>
								<b-form-input v-model="row.amount" type="number" :step="0.01" :min=0 required />
							</b-col>
							<b-col :cols=1 class="d-flex">
								<span class="my-auto">{{ translateUnit(row.unit) }}</span>
							</b-col>
							<b-col :cols=1>
								<b-button v-if="formProducts.length > 1" variant="danger" @click="removeProductRow(index)"><b-icon icon="x" scale="1.25" /></b-button>
							</b-col>
							<b-col :cols=11 class="mt-1">
								<b-form-input
									v-model="row.info"
									:placeholder="$t('task_form_placeholder_product_info')"
								/>
							</b-col>
						</b-form-row>
						<b-button variant="secondary" size="sm" @click="addProductRow">{{ $t('task_form_button_add_product_row') }}</b-button>
					</b-form-group>
					<b-form-group
						id="form_label_registration"
						:label="this.$t('task_form_label_registration')"
						label-for="form_registration"
					>
						<b-input-group class="mb-2" v-for="(row, index) in formRegistrations" :key="index">
							<b-form-input
								id="form_registration"
								v-model="row.number"
								required
								:placeholder="$t('task_form_placeholder_registration')"
							/>
							<b-input-group-append>
								<b-button v-if="formRegistrations.length > 1" variant="danger" @click="removeRegistrationRow(index)"><b-icon icon="x" scale="1.25" /></b-button>
							</b-input-group-append>
						</b-input-group>
						<b-button variant="secondary" size="sm" @click="addRegistrationRow">{{ $t('task_form_button_add_reg_row') }}</b-button>
					</b-form-group>
					<b-form-group
						v-if="task && task.description"
						id="form_label_description"
						:label="$t('task_form_label_description')"
						label-for="form_description"
					>
						<b-form-textarea
							id="form_description"
							v-model="formDescription"
							:placeholder="$t('task_form_placeholder_description')"
							rows="3"
						></b-form-textarea>
					</b-form-group>
                    <b-row class="justify-content-lg-center">
                        <b-col>
                            <hr />
                            <a :href="`/task`" type="button" class="btn btn-secondary">{{ $t('task_button_back_to_list') }}</a>
                            <b-button v-if="checkRole(user, 'ROLE_ADMIN') && task && !task.invoice" class="ml-2" @click="showDeleteModal" variant="danger">{{ $t('task_button_delete') }}</b-button>
                            <b-button v-if="checkRole(user, 'ROLE_ADMIN') || task && !task.invoice" class="ml-2" type="submit" variant="primary">{{ $t('task_button_save') }}</b-button>
                        </b-col>
                    </b-row>
                </b-form>
            </b-col>
        </b-row>

		<b-modal 
			v-if="checkRole(user, 'ROLE_ADMIN') && task && !task.invoice"
            ref="confirmDeleteModal" 
            size="md" 
            :title="$t('task_delete_modal_title')" 
            ok-only
            :ok-title="$t('task_delete_modal_button_ok')"
            @ok="deleteSubmit"
        >
			{{ $t('task_delete_modal_text') }}
		</b-modal>
	</div>
</template>

<script>
export default {
    name: 'TaskEdit',
    props: [
        'user',
        'checkRole',
        'task', 
		'washStations', 
		'customers', 
        'products', 
		'workers', 
		'filterCustomers',
		'filterProducts',
		'filterWorkers', 
        'taskEditCall', 
        'taskDeleteCall', 
        'dateFormat',
		'translateUnit',
        'calcDuration',
    ],
    data() {
        return {
			formWashStation: null,
			formStartTime: null,
			formEndTime: null,
			formCustomer: null,
			formDescription: null,
			formProducts: [],
			formRegistrations: [],
			formWorker: null,
			formNewCustomer: null,
			formDeletedRegistrations: [],
			formDeletedProducts: [],
			configs: {
				icons: {
					time: 'far fa-clock',
					date: 'far fa-calendar',
					up: 'fas fa-chevron-up',
					down: 'fas fa-chevron-down',
					previous: 'fas fa-chevron-left',
					next: 'fas fa-chevron-right',
					today: 'fas fa-calendar-check',
					clear: 'far fa-trash-alt',
					close: 'far fa-times-circle'
				},
				start: {
					locale: this.$i18n.locale ? this.$i18n.locale : process.env.VUE_APP_I18N_FALLBACK_LOCALE,
                    format: this.dateFormat,
					useCurrent: false,
					showClear: true,
					showClose: true,
                    sideBySide: true,
					minDate: false,
					maxDate: false,
				},
				end: {
					locale: this.$i18n.locale ? this.$i18n.locale : process.env.VUE_APP_I18N_FALLBACK_LOCALE,
                    format: this.dateFormat,
					useCurrent: false,
					showClear: true,
					showClose: true,
                    sideBySide: true,
					minDate: false
				},
			},
			taskDuration: null,
        }
    },
    methods: {
		setStartTime() {
			this.formStartTime = this.$moment(Date.now()).format(this.dateFormat)
		},
		cancelStartTime() {
			this.formStartTime = null
			this.formEndTime = null
		},
		setEndTime() {
			this.formEndTime = this.$moment(Date.now()).format(this.dateFormat)
		},
		cancelEndTime() {
			this.formEndTime = null
		},
		onStartChange(e) {
			this.$set(this.configs.end, 'minDate', e.date || null);
		},
		onEndChange(e) {
			this.$set(this.configs.start, 'maxDate', e.date || null);
		},
		showAddUserModal() {
            this.$refs['createCustomerModal'].show();
		},
		customerFormSubmit(data) {
			event.preventDefault();
			this.customerCreateCall(data);
			this.customerAdded = true;
            this.$refs['createCustomerModal'].hide();
		},
		addRegistrationRow() {
			let regRow = { number: null };
            this.formRegistrations.push(regRow);
		},
		removeRegistrationRow(index) {
			this.formDeletedRegistrations.push(this.formRegistrations[index].id);
			this.formRegistrations.splice(index, 1);
		},
		addProductRow() {
			let productRow = { product: null, amount: 1, unit: null, info: null };
            this.formProducts.push(productRow);
		},
		removeProductRow(index) {
			this.formDeletedProducts.push(this.formProducts[index].id);
            this.formProducts.splice(index, 1);
		},
		checkSelectedProducts(id) {
			let usedProductIds = [];
			this.formProducts.forEach(row => {
				usedProductIds.push(row.product);
			});
			return usedProductIds.includes(id);
		},
		setRowUnit(rowIndex, productId) {
			let product = this.products ? this.products.find(product => product.id == productId) : null;
			if (product) {
				this.formProducts[rowIndex].unit = product.unit;
			} else {
				this.formProducts[rowIndex].unit = null;
			}
		},
		workerSelectFilter: (options, search) => {
			let temp = search.toLowerCase();
            return options.filter(function (option) {
				let optionString = `${option.firstname.toLowerCase()} ${option.lastname.toLowerCase()}`;
				return optionString.includes(temp);
			});
		},
		productSelectFilter: (options, search) => {
			let temp = search.toLowerCase();
            return options.filter(function (option) {
				let optionString = "";
				if (option.code && option.name) {
					optionString = `${option.code.toLowerCase()} - ${option.name.toLowerCase()}`;
				} else {
					optionString = `${option.name ? option.name.toLowerCase() : option.name}`;
				}
				return optionString.includes(temp);
			});
		},
		validateForm() {
			var formValid = true;
			
			if (!this.formWashStation) {
				formValid = false;
			}
			if (!this.formProducts.length) {
				formValid = false;
			}
			this.formProducts.forEach(productRow => {
				if (!productRow.product) {
					formValid = false;
				}
				if (!productRow.amount) {
					formValid = false;
				}
			});
			if (!this.formStartTime) {
				formValid = false;
			}
			if (!this.formCustomer && this.formCustomer !== 0) {
				formValid = false;
			}
			if (this.formCustomer === 0 && !this.formNewCustomer) {
				formValid = false;
			}
			if (!this.formRegistrations.length) {
				formValid = false;
			}
			this.formRegistrations.forEach(regRow => {
				if (!regRow.number) {
					formValid = false;
				}
			});
			if (!this.formWorker) {
				formValid = false;
			}

			// role check
			if (this.task.confirmedAt && this.checkRole(this.user, 'ROLE_USER')) {
				formValid = false;
			}

			return formValid;
		},
		submit() {
            event.preventDefault();
			if (this.validateForm()) {
				let startTimestamp = this.$moment(this.formStartTime, this.dateFormat).format('x'),
					endTimestamp = this.$moment(this.formEndTime, this.dateFormat).format('x');
				this.taskEditCall(this.task.id, JSON.stringify(
					{
						washstation: this.formWashStation,
						products: this.formProducts,
						startTime: startTimestamp,
						endTime: endTimestamp,
						customer: this.formCustomer == 0 ? null : this.formCustomer,
						customerNew: this.formNewCustomer,
						registrations: this.formRegistrations,
						description: this.formDescription ? this.formDescription : '',
						worker: this.formWorker,
						deletedRegistrations: this.formDeletedRegistrations,
						deletedProducts: this.formDeletedProducts,
					}
				));
            }
        },
		showDeleteModal() {
            this.$refs['confirmDeleteModal'].show();
        },
		deleteSubmit() {
            this.taskDeleteCall(this.task.id);
        },
        setFormValues() {
            if (this.task) {
                this.formWashStation = this.task.washstation.id;
                this.formStartTime = this.$moment(this.task.startTime).format(this.dateFormat);
                this.formEndTime = this.$moment(this.task.endTime).format(this.dateFormat);
				this.formCustomer = this.task.customer ? this.task.customer.id : 0;
				this.formNewCustomer = this.task.customerNew;
                this.formDescription = this.task.description;
				this.formWorker = this.task.worker.id;

				this.task.products.forEach((product, index) => {
					this.formProducts.push(product);
					this.setRowUnit(index, product.product);
				});
				this.task.registrations.forEach(reg => {
					this.formRegistrations.push(reg);
				});
            }
		},
    },
    mounted() {
        this.setFormValues();
    },
    watch: {
        task: function () {
            if (this.task) {
                this.setFormValues();
            }
		},
		formStartTime() {
			if (this.formStartTime && this.formEndTime) {
				let start = this.$moment(this.formStartTime, this.dateFormat).format('x');
				let end = this.$moment(this.formEndTime, this.dateFormat).format('x')
				if (start < end) {
					this.taskDuration = this.calcDuration(start, end);
				}
			}
		},
		formEndTime() {
			if (this.formStartTime && this.formEndTime) {
				let start = this.$moment(this.formStartTime, this.dateFormat).format('x');
				let end = this.$moment(this.formEndTime, this.dateFormat).format('x')
				if (start < end) {
					this.taskDuration = this.calcDuration(start, end);
				}
			}
		},
    },
}
</script>